import { css } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, space, text } from "src/theme/ui";

/**
 * How a table looks in this product.
 *
 * Lifted out of the dashboard's own table so that any other table can wear
 * it. It was declared inside that one component, so every table elsewhere in
 * the app fell back to Ant Design's defaults — ruled rows, grey headings in
 * the library's type, square hover — and read as a different product from
 * the one on the overview page.
 *
 * The header is a tinted band of small caps; rows carry no rules between
 * them, because the band and the row padding do the separating; and the hover
 * lands as one rounded block, so a long row reads as a single object rather
 * than a grid of boxes.
 */
export const tableChrome = (gutter = 16) => css`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    padding-left: ${gutter}px;
  }

  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    padding-right: ${gutter}px;
  }

  .ant-table-thead > tr > th {
    padding: 8px 12px;
    background: ${colors.gray[50]};
    border-bottom: 1px solid ${line.hairline};
    color: ${colors.gray[500]};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  /* The library draws a divider between headings out of a pseudo-element. */
  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-column-sorter {
    color: ${colors.gray[300]};
  }

  .ant-table-column-sorter-up.active,
  .ant-table-column-sorter-down.active {
    color: ${colors.primary[500]};
  }

  .ant-table-tbody > tr > td {
    padding: 9px 12px;
    border-bottom: 0;
    color: ${colors.gray[800]};
    font-size: 13px;
    ${text.numeric}
    transition: background 0.15s ease;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${colors.primary[10]};
  }

  .ant-table-tbody > tr.ant-table-row > td:first-child {
    border-top-left-radius: ${radii.md}px;
    border-bottom-left-radius: ${radii.md}px;
  }

  .ant-table-tbody > tr.ant-table-row > td:last-child {
    border-top-right-radius: ${radii.md}px;
    border-bottom-right-radius: ${radii.md}px;
  }

  /* The name and the latest value are what the eye should land on. */
  .ant-table-tbody > tr > td b,
  .ant-table-tbody > tr > td.cell-emphasis {
    color: ${colors.gray[900]};
    font-weight: 600;
  }

  .ant-table-placeholder .ant-table-cell {
    border-bottom: 0;
  }

  .ant-table-placeholder .ant-empty {
    padding: ${space["3xl"]}px 0;
  }
`;

/**
 * The pager under a table.
 *
 * Shared for the same reason the table is: it was declared inside the
 * dashboard's own table and nowhere else, so a pager anywhere else in the app
 * came out as Ant Design draws one.
 */
export const tablePagination = css`
  .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: ${space.md}px 16px;
    border-top: 1px solid ${line.hairline};
    font-size: 13px;
    align-items: center;
  }
  .ant-pagination-total-text {
    margin-right: auto;
    color: ${colors.gray[500]};
  }
  /* Football's pagination, on Ant Design's control: 32px on a 9px corner, a
     ring rather than a border so a row of pages stays evenly spaced, and the
     current page a SOLID accent fill. That design allows one lit surface per
     control and this is it — a tint here (which is what this was) reads as a
     hover that will not go away. */
  .ant-table-pagination .ant-pagination-item,
  .ant-table-pagination .ant-pagination-prev,
  .ant-table-pagination .ant-pagination-next {
    min-width: 32px;
    height: 32px;
    margin: 0 2px;
    border: 0;
    border-radius: 9px;
    background: ${colors.white};
    box-shadow: inset 0 0 0 1px ${line.field};
    line-height: 32px;
  }
  .ant-table-pagination .ant-pagination-item a {
    ${text.numeric}
    font-size: 12.5px;
    font-weight: 500;
    color: ${colors.gray[700]};
  }
  .ant-table-pagination .ant-pagination-item:hover,
  .ant-table-pagination .ant-pagination-prev:hover,
  .ant-table-pagination .ant-pagination-next:hover {
    background: ${colors.gray[50]};
  }
  .ant-table-pagination .ant-pagination-item-active,
  .ant-table-pagination .ant-pagination-item-active:hover {
    background: ${colors.primary[500]};
    box-shadow: none;
  }
  .ant-table-pagination .ant-pagination-item-active a,
  .ant-table-pagination .ant-pagination-item-active:hover a {
    color: ${colors.white};
    font-weight: 500;
  }
  /* Flex-centred, not left to the line box. The arrow is an icon in a text
     line otherwise, so it sits on that line's baseline — which put both
     chevrons two pixels above the centre of their own button while the page
     number beside them was centred correctly. */
  .ant-table-pagination .ant-pagination-item-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: ${colors.gray[700]};
  }
  .ant-table-pagination .ant-pagination-item-link .anticon {
    display: flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }
  /* The gap between page runs is a mark, not a button.
     Flex-centred, and the library's own ellipsis un-positioned: it places
     that glyph absolutely with a top offset of its own, which dropped the
     three dots to the bottom edge of a box the rest of the pager centres
     in. */
  .ant-table-pagination .ant-pagination-jump-prev,
  .ant-table-pagination .ant-pagination-jump-next {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 32px;
  }
  &&
    .ant-table-pagination
    .ant-pagination-jump-prev
    .ant-pagination-item-container,
  &&
    .ant-table-pagination
    .ant-pagination-jump-next
    .ant-pagination-item-container,
  &&
    .ant-table-pagination
    .ant-pagination-jump-prev
    .ant-pagination-item-ellipsis,
  &&
    .ant-table-pagination
    .ant-pagination-jump-next
    .ant-pagination-item-ellipsis {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin: 0;
    letter-spacing: 1px;
    color: ${colors.gray[400]};
  }
  /* Football's page-size control: 32px on a 9px corner, a 12px label, and a
     ring in the field's edge. */
  .ant-table-pagination .ant-pagination-options .ant-select-selector {
    height: 32px;
    padding: 0 9px;
    border: 1px solid ${line.field};
    border-radius: 9px;
    background: ${colors.white};
  }
  /* The label arrived with 7px of padding on its left and 21px on its right,
     which put "6 / page" visibly left of centre in its own box. Balanced
     against the chevron's own inset instead. */
  .ant-table-pagination .ant-pagination-options .ant-select-selection-item {
    padding-right: 15px;
  }
  /* Doubled up: the library scopes the label's line-height to the select's
     SIZE class, which outranked a plain rule here — so the box was 32px
     while its text still sat on a 22px line. */
  && .ant-table-pagination .ant-pagination-options .ant-select-selector::after,
  && .ant-table-pagination .ant-pagination-options .ant-select-selection-item {
    ${text.numeric}
    line-height: 30px;
    font-size: 12px;
    color: ${colors.gray[800]};
  }
  .ant-table-pagination .ant-pagination-options .ant-select-arrow {
    right: 8px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
    color: ${colors.gray[500]};
  }
`;

export default tableChrome;
